import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { NgForm } from '@angular/forms';
import { MatChipInputEvent } from '@angular/material';
import { ShippingOption, ShippingOptionsApiService } from 'src/app/api';
import { TextMaskConfig } from 'angular2-text-mask';
import { of, zip } from 'rxjs';
import { reduce, switchMap, tap } from 'rxjs/operators';
import { createNumberMask } from 'text-mask-addons/dist/textMaskAddons';
import { DePara, IntegrationApiService, ShippingOptionsMapping } from '../../../services/api/integration-api.service';

@Component({
  selector: 'mandae-integration-setup-properties',
  templateUrl: './integration-setup-properties.component.html',
  styleUrls: ['./integration-setup-properties.component.scss', '../integration-setup-dialog.shared.scss']
})
export class IntegrationSetupPropertiesComponent implements OnInit {

  @Input() provider: any = {};
  @Input() stepCount = 0;
  @Input() stepNumber = 0;

  @Output() back = new EventEmitter();
  @Output() next = new EventEmitter();

  public get hasStatus(): boolean {
    return this.provider && this.provider.config && this.provider.config.status;
  }

  public get hasAutomaticShippingOptionSelection(): boolean {
    return this.provider && this.provider.config && this.provider.config.matches;
  }

  public get hasInsurance(): boolean {
    return this.provider && this.provider.config && this.provider.config.insurance;
  }

  public get showCustomStatusField(): boolean {
    return this.selectedStatus && this.selectedStatus.codigo === '__custom';
  }

  public loadingStatus = false;
  public orderStatus: any[] = null;
  public selectedStatus: any = null;
  public customStatusCode = '';

  public shippingOptions: ShippingOption[] = [];
  public shippingOptionsMapping: ShippingOptionsMapping = {};

  public enableAutomaticInsurance = false;
  public allShippingsInsurance = true;
  public minSaleValueForInsurance = '';
  public textMaskConfig: TextMaskConfig = {
    placeholderChar: '0',
    guide: true,
    mask: createNumberMask({
      prefix: 'R$ ',
      thousandsSeparatorSymbol: '.',
      allowDecimal: true,
      decimalSymbol: ',',
      requireDecimal: true,
    }),
  };

  public readonly separatorKeysCodes: number[] = [ENTER, COMMA];

  constructor(
    private integrationApi: IntegrationApiService,
    private shippingOptionsApi: ShippingOptionsApiService,
  ) { }

  public ngOnInit(): void {
    this.loadOrderStatus();
    this.loadAutomaticShippingOptionSelection();
  }

  public trackByOrderCode(index, item): string {
    return item.codigo;
  }

  public addShippingOptionMapping($event: MatChipInputEvent, shippingOption: string): void {
    const value = ($event.value || '').trim();

    if (!value) {
      return;
    }

    if (this.shippingOptionsMapping[shippingOption].find(dePara => dePara.de.toLocaleUpperCase() === value.toLocaleUpperCase())) {
      return;
    }

    this.shippingOptionsMapping[shippingOption].push({ de: value, para: shippingOption });
    $event.input.value = '';
  }

  public removeShippingOptionMapping(mapping: DePara, shippingOption: string): void {
    const index = this.shippingOptionsMapping[shippingOption]
      .findIndex(item => item.de.toLocaleUpperCase() === mapping.de.toLocaleUpperCase());

    if (index >= 0) {
      this.shippingOptionsMapping[shippingOption].splice(index, 1);
    }
  }

  public handleClickBack(): void {
    this.back.emit();
  }

  public save(form: NgForm): void {
    if (form.invalid) {
      return;
    }

    let orderStatus$ = of(null);
    if (this.hasStatus) {
      const orderStatus = this.showCustomStatusField ? this.customStatusCode : this.selectedStatus.codigo;
      orderStatus$ = this.integrationApi.setOrderStatus(this.provider.slug, orderStatus);
    }

    let automaticInsurance$ = of(null);
    if (this.hasInsurance) {
      const minSaleValue = +this.minSaleValueForInsurance.replace('R$ ', '').replace('.', '').replace(',', '.');
      automaticInsurance$ = this.integrationApi.setAutomaticInsurance(this.provider.slug, {
        preencherTodosEnvios: this.allShippingsInsurance,
        preencherValorDeclarado: this.enableAutomaticInsurance,
        valorMinimoDeclarado: this.allShippingsInsurance ? 0 : minSaleValue,
      });
    }

    let shippingOptionsMapping$ = of(null);
    if (this.hasAutomaticShippingOptionSelection) {
      shippingOptionsMapping$ = this.integrationApi.getAutomaticShippingMapping(this.provider.slug).pipe(
        switchMap((storedOptions: ShippingOptionsMapping) => {
          const diff = (A: ShippingOptionsMapping, B: ShippingOptionsMapping) => Object.keys(A)
            .map(option => {
              if (!B[option]) {
                return A[option];
              }

              return A[option].filter(dePara => B[option].find(x => x.de === dePara.de));
            });

          const optionsToDelete = diff(storedOptions, this.shippingOptionsMapping)
            .reduce((acc, val) => acc.concat(val), [])
            .map(option => option.id);
          const optionsToAdd = diff(this.shippingOptionsMapping, storedOptions)
            .reduce((acc, val) => acc.concat(val), []);

          return zip(
            this.integrationApi.deleteAutomaticShippingMapping(this.provider.slug, optionsToDelete),
            this.integrationApi.addAutomaticShippingMapping(this.provider.slug, optionsToAdd),
          );
        }),
      );
    }

    zip(orderStatus$, automaticInsurance$, shippingOptionsMapping$).subscribe(
      () => this.next.emit(),
    );
  }

  private loadOrderStatus(): void {
    if (!this.hasStatus) {
      return;
    }

    this.loadingStatus = true;
    this.integrationApi.getOrderStatus(this.provider.slug)
      .subscribe(
        (data: any) => {
          this.orderStatus = data.disponiveis;

          this.selectedStatus = data.selecionado;
          if (!this.orderStatus.some(status => this.selectedStatus.codigo.toLocaleUpperCase() === status.codigo.toLocaleUpperCase())) {
            this.orderStatus.push(this.selectedStatus);
          }

          if (data.permiteStatusPersonalizado) {
            this.orderStatus.push({
              codigo: '__custom',
              descricao: 'Definir status personalizado...',
            });
          }
        },
        () => {},
        () => {
          this.loadingStatus = false;
        }
      );
  }

  private loadAutomaticShippingOptionSelection(): void {
    if (!this.hasAutomaticShippingOptionSelection) {
      return;
    }

    this.shippingOptionsApi.getShippingOptions()
      .pipe(
        tap(options => this.shippingOptions = options),
        switchMap(options => options),
        reduce((acc: any, val: any) => {
          acc[val.servicoEnvio] = [];
          return acc;
        }, {}),
        tap(mappings => this.shippingOptionsMapping = mappings),
        switchMap(() => this.integrationApi.getAutomaticShippingMapping(this.provider.slug)),
        switchMap((data: any) => data.dePara),
        tap((dePara: DePara) => this.shippingOptionsMapping[dePara.para].push(dePara)),
      )
      .subscribe();
  }
}
